import React, { useState, useRef, useEffect } from 'react';
import { Clock, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// Generate time slots every 15 minutes
const TIME_SLOTS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_SLOTS.push(
      `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    );
  }
}

export default function TimePicker({ value, onChange, disabled, placeholder }) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Scroll selected time into view when opening
  useEffect(() => {
    if (!open || !listRef.current) return;
    const selected = listRef.current.querySelector('[data-selected="true"]');
    if (selected) selected.scrollIntoView({ block: 'center' });
  }, [open]);

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const isDark = theme.background !== 'bg-white';

  return (
    <div ref={containerRef} className="relative">
      {/* Input */}
      <div
        onClick={() => !disabled && setOpen(prev => !prev)}
        className={`flex items-center w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.input} ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span className={`flex-1 ${!value ? 'text-gray-400' : ''}`}>
          {value || placeholder || 'Select time'}
        </span>
        <div className="flex items-center gap-1 ml-2">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Clear"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
          <Clock className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={`absolute z-50 mt-1 w-32 rounded-lg shadow-lg border overflow-hidden ${
            isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          {/* Time list */}
          <div ref={listRef} className="max-h-48 overflow-y-auto">
            {TIME_SLOTS.map((slot) => (
              <button
                key={slot}
                type="button"
                data-selected={value === slot}
                onClick={() => { onChange(slot); setOpen(false); }}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  value === slot
                    ? 'bg-blue-600 text-white font-medium'
                    : isDark
                      ? 'text-gray-200 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {slot}
              </button>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}
